import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { useHistory, useLocation } from 'react-router-dom';

import config from '../../../config';
import { takeOAuthAttempt, finishAuthAttempt } from '../../../utils/authAnalytics';
import showSpotlightToast from '../../ToastDialog/showSpotlightToast';
import {
  ONBOARDING_SIGNUP_EVENTS,
  trackOnboardingSignup,
} from '../../../utils/onboardingSignupAnalytics';

const MESSAGE_TYPE_PARAM = 'message_type';
const ERROR_PARAM = 'error';
const TOAST_DELAY = 250;

const FALLBACK_MESSAGES = {
  oauth_cancelled_error: 'Sign-in was cancelled. Please try again.',
};

const isTrue = (value) => value === true || `${value}`.toLowerCase() === 'true';

const parseMessageCookie = (rawValue) => {
  if (!rawValue) return null;

  try {
    return JSON.parse(rawValue);
  } catch {
    try {
      return JSON.parse(decodeURIComponent(rawValue));
    } catch {
      return null;
    }
  }
};

const removeMessagesCookie = () => {
  if (!config.messagesKey) return;

  Cookies.remove(config.messagesKey, { path: '/' });
  Cookies.remove(config.messagesKey, {
    domain: window.location.hostname,
    path: '/',
  });
  Cookies.remove(config.messagesKey, {
    domain: '.spotlightnews.us',
    path: '/',
  });
};

const OAuthRedirectMessage = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const [pendingMessage, setPendingMessage] = useState(null);
  const params = new URLSearchParams(location.search);
  const messageType = params.get(MESSAGE_TYPE_PARAM);

  // Capture the OAuth callback first. Do not display the toast in this same
  // pass because a signed-out callback can immediately navigate
  // / -> /news -> /signup. The message is kept in component state until the
  // routing chain settles, then displayed on the destination screen.
  useEffect(() => {
    if (!messageType) return;
    if (messageType.endsWith('_error')) {
      const cancelled = messageType === 'oauth_cancelled_error';
      finishAuthAttempt(takeOAuthAttempt(), cancelled ? 'cancelled' : 'failure',
        cancelled ? 'provider_cancelled' : 'provider_error');
    }

    const rawCookie = config.messagesKey ? Cookies.get(config.messagesKey) : null;
    const cookieMessage = parseMessageCookie(rawCookie);
    const cookieMessageType = cookieMessage?.message_type;
    const hasMatchingCookie = cookieMessageType === messageType;
    const message = hasMatchingCookie
      ? cookieMessage?.message
      : FALLBACK_MESSAGES[messageType];

    if (message) {
      const isError = isTrue(params.get(ERROR_PARAM))
        || (hasMatchingCookie && isTrue(cookieMessage?.error))
        || messageType.endsWith('_error');

      try {
        const signupMethod = window.sessionStorage
          .getItem('spotlight_pending_oauth_signup_provider');

        if (!isError && signupMethod) {
          trackOnboardingSignup(ONBOARDING_SIGNUP_EVENTS.succeeded, {
            signup_method: signupMethod,
          });
        }

        if (signupMethod) {
          window.sessionStorage.removeItem('spotlight_pending_oauth_signup_provider');
        }
      } catch {
        // Authentication must continue even if analytics storage is unavailable.
      }

      setPendingMessage({
        createdAt: Date.now(),
        isError,
        message: `${message}`,
        messageType,
      });
    } else {
      setPendingMessage(null);
    }

    if (hasMatchingCookie) {
      removeMessagesCookie();
    } else if (rawCookie && !cookieMessage) {
      removeMessagesCookie();
    }

    const cleanParams = new URLSearchParams(location.search);
    cleanParams.delete(MESSAGE_TYPE_PARAM);
    cleanParams.delete(ERROR_PARAM);

    const nextSearch = cleanParams.toString();

    // OAuth message handling should not decide where the user goes next.
    // Keep the pathname unchanged and let the existing router/auth guards own
    // the normal / -> /news -> /signup or onboarding navigation.
    history.replace({
      hash: location.hash,
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      state: location.state,
    });
  }, [
    history,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    messageType,
  ]);

  // Once the trigger is gone, wait briefly for any auth redirect to settle.
  // The timer is cancelled/restarted whenever the pathname changes. This is
  // what makes the cancellation case reliable for signed-out users.
  useEffect(() => {
    if (messageType || !pendingMessage) return undefined;

    const timer = window.setTimeout(() => {
      const isError = Boolean(pendingMessage.isError);

      showSpotlightToast({
        activateText: isError ? 'Something went wrong' : 'Success',
        title: pendingMessage.message,
        toastId: `oauth-${pendingMessage.messageType}-${pendingMessage.createdAt}`,
        type: isError ? 'error' : 'success',
      });

      setPendingMessage(null);
    }, TOAST_DELAY);

    return () => window.clearTimeout(timer);
  }, [location.pathname, messageType, pendingMessage]);

  // Keep Initialize/MainRouter unmounted until the one-shot callback has been
  // captured; otherwise the root redirect can consume the URL first.
  if (messageType) return null;

  return children;
};

OAuthRedirectMessage.propTypes = {
  children: PropTypes.node.isRequired,
};

export default OAuthRedirectMessage;
